import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { useApplication } from "@/contexts/ApplicationContext";
import * as Sentry from "@sentry/react";
import {
  DriverOnroadTestAssessment,
  AssessmentUpdateData,
  createDriverOnroadTestAssessment,
  updateDriverOnroadTestAssessment,
} from "@/services/driverOnroadTestService";

// Local storage key for assessment ID
const ASSESSMENT_ID_KEY = "driverOnroadTestAssessmentId";

/**
 * Custom hook for managing driver onroad test assessments
 *
 * This hook handles:
 * 1. Creating a new assessment at the start of the test
 * 2. Updating the assessment with question progress
 * 3. Completing the assessment at the end of the test
 */
export const useDriverOnroadTestAssessment = () => {
  const queryClient = useQueryClient();
  const { application } = useApplication();
  const [assessmentId, setAssessmentId] = useState<string | null>(
    localStorage.getItem(ASSESSMENT_ID_KEY)
  );

  // Mutation for creating a new assessment
  const createAssessmentMutation = useMutation({
    mutationFn: createDriverOnroadTestAssessment,
    onSuccess: (data) => {
      if (data.success && data.assessment?.id) {
        // Save assessment ID to local storage
        localStorage.setItem(ASSESSMENT_ID_KEY, data.assessment.id);
        setAssessmentId(data.assessment.id);
      }
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: {
          context: "useDriverOnroadTestAssessment.createAssessmentMutation",
          applicationId: application?.applicationId,
        },
      });
    },
  });

  // Mutation for updating an existing assessment
  const updateAssessmentMutation = useMutation({
    mutationFn: updateDriverOnroadTestAssessment,
    onSuccess: (data) => {
      if (data.success && data.assessment) {
        // Update assessment data in cache if needed
        queryClient.setQueryData(
          ["driverOnroadTestAssessment", data.assessment.id],
          data.assessment
        );
      }
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: {
          context: "useDriverOnroadTestAssessment.updateAssessmentMutation",
          assessmentId,
        },
      });
    },
  });

  /**
   * Start a new driver onroad test assessment
   * @param totalQuestions Total number of questions in the test
   */
  const startAssessment = useCallback(async () => {
    if (!application?.applicationId) {
      console.error("Cannot start assessment: No application ID available");
      return {
        success: false,
        message: "Application ID is required",
      };
    }

    try {
      // Create assessment data
      const assessmentData: Omit<DriverOnroadTestAssessment, "id"> = {
        application_id: application.applicationId,
        started_at: new Date().toISOString(),
        question_num: 1,
        type: "driver-onroad-test",
      };

      // Create the assessment
      const result = await createAssessmentMutation.mutateAsync(assessmentData);

      if (result.success && result.assessment?.id) {
        return {
          success: true,
          assessmentId: result.assessment.id,
          message: "Assessment created successfully",
        };
      }

      return {
        success: false,
        message: result.message || "Failed to create assessment",
      };
    } catch (error) {
      console.error("Error starting driver onroad test assessment:", error);
      Sentry.captureException(error, {
        extra: {
          context: "useDriverOnroadTestAssessment.startAssessment",
          applicationId: application?.applicationId,
        },
      });
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }, [application?.applicationId, createAssessmentMutation]);

  /**
   * Update the current question in the assessment
   * @param questionNumber Current question number
   */
  const updateQuestion = useCallback(
    async (questionNumber: number) => {
      if (!assessmentId) {
        console.error("Cannot update question: No assessment ID available");
        return {
          success: false,
          message: "No assessment ID available",
        };
      }

      try {
        const updateData: AssessmentUpdateData = {
          id: assessmentId,
          question_num: questionNumber,
        };

        const result = await updateAssessmentMutation.mutateAsync(updateData);
        return {
          success: result.success,
          message: result.message,
          assessment: result.assessment,
        };
      } catch (error) {
        console.error("Error updating driver onroad test question:", error);
        Sentry.captureException(error, {
          extra: {
            context: "useDriverOnroadTestAssessment.updateQuestion",
            assessmentId,
            questionNumber,
          },
        });
        return {
          success: false,
          message:
            error instanceof Error ? error.message : "Unknown error occurred",
        };
      }
    },
    [assessmentId, updateAssessmentMutation]
  );

  return {
    assessmentId,
    isLoading:
      createAssessmentMutation.isPending || updateAssessmentMutation.isPending,
    error: createAssessmentMutation.error || updateAssessmentMutation.error,
    startAssessment,
    updateQuestion,
  };
};

export default useDriverOnroadTestAssessment;
